<template>
  <div class="gaia-chatbot">
    <div class="chatbot-window" v-show="isOpen" :style="windowStyle">

      <div class="resize-edge resize-edge--top"    @mousedown.prevent="onResizeStart($event, 'top')"></div>
      <div class="resize-corner resize-corner--tl" @mousedown.prevent="onResizeStart($event, 'corner')"></div>

      <div class="chatbot-header">
        <div class="gaia-avatar">
          <i class="material-icons">eco</i>
        </div>
        <span class="header-title ml-2">GAIA</span>
        <div class="ml-auto d-flex align-items-center">
          <button class="header-action-btn" @click="newConversation" title="New conversation">
            <i class="material-icons">refresh</i>
          </button>
          <button class="close-btn ml-1" @click="isOpen = false">
            <i class="material-icons">close</i>
          </button>
        </div>
      </div>

      <div class="chatbot-messages" ref="messagesContainer">
        <div
          v-for="(msg, index) in messages"
          :key="index"
          :class="['chatbot-message', msg.role === 'user' ? 'user-message' : 'bot-message', msg.isError ? 'error-message' : '']"
        >
          <div v-if="msg.role === 'bot'" class="message-bubble" v-html="parseMarkdown(msg.text)"></div>
          <div v-else class="message-bubble">{{ msg.text }}</div>
          <button
            v-if="msg.isError && index === messages.length - 1 && lastFailed"
            class="retry-btn"
            @click="retryLast"
          >
            <i class="material-icons">refresh</i> Retry
          </button>
        </div>
        <div v-if="loading" class="chatbot-message bot-message">
          <div class="message-bubble typing-indicator">
            <span></span><span></span><span></span>
          </div>
        </div>
      </div>

      <div class="chatbot-chips" v-if="showChips">
        <button
          v-for="action in quickActions"
          :key="action.label"
          class="chip-btn"
          :disabled="loading"
          @click="sendMessage(action.message)"
        >
          {{ action.label }}
        </button>
      </div>

      <div class="chatbot-input">
        <input
          v-model="inputText"
          type="text"
          placeholder="Speak with GAIA..."
          @keyup.enter="sendMessage()"
          :disabled="loading"
          ref="chatInput"
        />
        <button
          class="send-btn"
          @click="sendMessage()"
          :disabled="loading || !inputText.trim()"
        >
          <i class="material-icons">send</i>
        </button>
      </div>

    </div>

    <button class="chatbot-toggle" @click="toggleChat" :class="{ active: isOpen }">
      <span v-if="hasUnread && !isOpen" class="unread-badge"></span>
      <i class="material-icons">{{ isOpen ? 'close' : 'eco' }}</i>
    </button>
  </div>
</template>

<script>
import { sendChatMessage } from '@/api/index';

const STORAGE_KEY = 'gaia-chat';

const MIN_W = 280;
const MAX_W = 680;
const MIN_H = 320;
const MAX_H = 820;

const QUICK_ACTIONS = [
  { label: '📍 Field Status',  message: 'Give me a quick overview of my sensors and the weather please.' },
  { label: '💧 Soil Moisture', message: 'According to the most recent sensor and weather data, what is the overall status of my soil?' },
  { label: '🌱 Thresholds',    message: 'Give me a quick threshold analysis for my sensors.' },
  { label: '⚠️ Active Alerts', message: 'Give me a summary of my active alerts.' },
];

export default {
  name: 'Chatbot',
  data() {
    return {
      isOpen: false,
      inputText: '',
      messages: [],
      loading: false,
      hasUnread: false,
      lastFailed: null,
      sessionId: null,
      quickActions: QUICK_ACTIONS,
      size: { width: 340, height: 480 },
    };
  },
  watch: {
    '$store.state.course.course_id'(newId, oldId) {
      if (oldId && newId !== oldId) this.newConversation();
    },
  },
  computed: {
    greetingMessage() {
      const name = this.$store.state.user && this.$store.state.user.name;
      const hi = name ? `Hi ${name}!` : 'Hi!';
      return `${hi} I'm Gaia, your Groundworx field assistant.\nAsk me about your sensors, alerts, weather, or thresholds.`;
    },
    showChips() {
      return this.messages.length <= 1;
    },
    windowStyle() {
      return { width: this.size.width + 'px', height: this.size.height + 'px' };
    },
  },
  created() {
    this.loadFromStorage();
  },
  methods: {
    loadFromStorage() {
      try {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        if (saved) {
          const { msgs, sid } = JSON.parse(saved);
          if (msgs && msgs.length) {
            this.messages = msgs;
            if (sid) this.sessionId = sid;
            return;
          }
        }
      } catch (e) {
        // ignore
      }
      if (!this.sessionId) {
        this.sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
      }
      this.messages = [{ role: 'bot', text: this.greetingMessage }];
    },
    saveToStorage() {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ msgs: this.messages, sid: this.sessionId }));
      } catch (e) {
        // ignore
      }
    },
    newConversation() {
      sessionStorage.removeItem(STORAGE_KEY);
      this.sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
      this.messages = [{ role: 'bot', text: this.greetingMessage }];
      this.lastFailed = null;
    },
    toggleChat() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.hasUnread = false;
        this.$nextTick(() => {
          this.scrollToBottom();
          if (this.$refs.chatInput) this.$refs.chatInput.focus();
        });
      }
    },
    async sendMessage(overrideText) {
      const trimmed = (overrideText !== undefined ? overrideText : this.inputText).trim();
      if (!trimmed || this.loading) return;

      this.messages.push({ role: 'user', text: trimmed });
      if (overrideText === undefined) this.inputText = '';
      this.loading = true;
      this.lastFailed = null;
      this.$nextTick(() => this.scrollToBottom());
      this.saveToStorage();

      try {
        const courseId = this.$store.state.course && this.$store.state.course.course_id;
        if (!courseId) {
          this.messages.push({ role: 'bot', text: 'Please select a facility using the dropdown at the top of the page first, then I\'ll be ready to help.' });
          this.loading = false;
          this.$nextTick(() => this.scrollToBottom());
          this.saveToStorage();
          return;
        }
        const res = await sendChatMessage(trimmed, this.sessionId, courseId);
        const botText = (res && (res.message || res.response)) || 'Sorry, I could not process your request.';
        this.messages.push({ role: 'bot', text: botText });
        if (!this.isOpen) this.hasUnread = true;
      } catch (err) {
        console.error('[GAIA] error:', err);
        this.lastFailed = trimmed;
        this.messages.push({ role: 'bot', text: 'Connection error — please try again.', isError: true });
      } finally {
        this.loading = false;
        this.$nextTick(() => this.scrollToBottom());
        this.saveToStorage();
      }
    },
    async retryLast() {
      const text = this.lastFailed;
      this.lastFailed = null;
      this.messages.pop();
      await this.sendMessage(text);
    },
    scrollToBottom() {
      const el = this.$refs.messagesContainer;
      if (el) el.scrollTop = el.scrollHeight;
    },
    onResizeStart(e, edge) {
      const startX = e.clientX;
      const startY = e.clientY;
      const startW = this.size.width;
      const startH = this.size.height;

      const onMove = (moveE) => {
        const dx = moveE.clientX - startX;
        const dy = moveE.clientY - startY;
        if (edge === 'top' || edge === 'corner') {
          this.size.height = Math.max(MIN_H, Math.min(MAX_H, startH - dy));
        }
        if (edge === 'corner') {
          this.size.width = Math.max(MIN_W, Math.min(MAX_W, startW - dx));
        }
      };

      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    parseMarkdown(text) {
      if (!text) return '';
      // Escape HTML to prevent XSS before applying transforms
      let html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      // Bold
      html = html.replace(/\*\*(.*?)\*\*/gs, '<strong>$1</strong>');
      // Inline code
      html = html.replace(/`([^`\n]+)`/g, '<code>$1</code>');
      // Bullet lists
      const lines = html.split('\n');
      const out = [];
      let inList = false;
      for (const line of lines) {
        if (/^[-•*]\s+/.test(line)) {
          if (!inList) { out.push('<ul>'); inList = true; }
          out.push('<li>' + line.replace(/^[-•*]\s+/, '') + '</li>');
        } else {
          if (inList) { out.push('</ul>'); inList = false; }
          out.push(line);
        }
      }
      if (inList) out.push('</ul>');
      html = out.join('\n')
        .replace(/\n\n+/g, '</p><p>')
        .replace(/\n/g, '<br>');
      return '<p>' + html + '</p>';
    },
  },
};
</script>

<style lang="scss" scoped>
// ── GAIA Design Tokens ──────────────────────────────────────────────
$gaia-green:      #28a745;
$gaia-green-dark: #1e7e34;
$gaia-blue:       #1b86c8;
$gaia-blue-dark:  #1570a8;
$gaia-earth:      #F9F7F2;
$gaia-earth-dark: #ede9e0;
$gaia-text:       #2d3b2e;

// ── Pulse keyframes (toggle button heartbeat) ─────────────────────
@keyframes gaia-pulse {
  0%   { box-shadow: 0 0 0 0   rgba($gaia-green, 0.65); }
  70%  { box-shadow: 0 0 0 14px rgba($gaia-green, 0);   }
  100% { box-shadow: 0 0 0 0   rgba($gaia-green, 0);    }
}

// ── Typing bounce ─────────────────────────────────────────────────
@keyframes typing-bounce {
  0%, 60%, 100% { transform: translateY(0);   }
  30%           { transform: translateY(-6px); }
}

// ── Root container ────────────────────────────────────────────────
.gaia-chatbot {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  // ── Floating action button ──────────────────────────────────────
  .chatbot-toggle {
    position: relative;
    width: 58px;
    height: 58px;
    border-radius: 50%;
    background: $gaia-green;
    border: none;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 14px rgba($gaia-green, 0.45);
    transition: background 0.25s, transform 0.2s;
    animation: gaia-pulse 2.4s ease-out infinite;

    &:hover {
      background: $gaia-green-dark;
      transform: scale(1.07);
    }

    &.active {
      background: #495057;
      animation: none;
      box-shadow: 0 4px 14px rgba(0,0,0,0.25);
    }

    .material-icons { font-size: 27px; }

    .unread-badge {
      position: absolute;
      top: 4px;
      right: 4px;
      width: 12px;
      height: 12px;
      background: #dc3545;
      border-radius: 50%;
      border: 2px solid white;
    }
  }

  // ── Chat window ─────────────────────────────────────────────────
  .chatbot-window {
    position: relative;
    background: $gaia-earth;
    border-radius: 16px;
    box-shadow: 0 10px 36px rgba(0, 0, 0, 0.18);
    display: flex;
    flex-direction: column;
    margin-bottom: 12px;
    overflow: hidden;

    // ── Resize handles ───────────────────────────────────────────
    .resize-edge--top {
      position: absolute;
      top: 0;
      left: 20px;
      right: 0;
      height: 6px;
      cursor: n-resize;
      z-index: 10;
    }

    .resize-corner--tl {
      position: absolute;
      top: 0;
      left: 0;
      width: 20px;
      height: 20px;
      cursor: nw-resize;
      z-index: 11;

    }

    // ── Header ───────────────────────────────────────────────────
    .chatbot-header {
      background: linear-gradient(135deg, $gaia-blue 0%, $gaia-green 100%);
      color: white;
      padding: 11px 14px;
      display: flex;
      align-items: center;
      flex-shrink: 0;

      .gaia-avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: rgba(white, 0.2);
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1.5px solid rgba(white, 0.5);

        .material-icons {
          font-size: 18px;
          color: white;
        }
      }

      .header-title {
        font-weight: 700;
        font-size: 16px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }

      .header-action-btn,
      .close-btn {
        background: none;
        border: none;
        color: white;
        padding: 0;
        cursor: pointer;
        display: flex;
        align-items: center;
        opacity: 0.75;
        transition: opacity 0.15s;

        &:hover { opacity: 1; }
        .material-icons { font-size: 20px; }
      }
    }

    // ── Messages area ────────────────────────────────────────────
    .chatbot-messages {
      flex: 1;
      overflow-y: auto;
      padding: 14px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      background: $gaia-earth;
      min-height: 0;

      &::-webkit-scrollbar { width: 5px; }
      &::-webkit-scrollbar-track { background: $gaia-earth-dark; }
      &::-webkit-scrollbar-thumb { background: rgba($gaia-green, 0.35); border-radius: 4px; }

      // ── Message bubbles ────────────────────────────────────────
      .chatbot-message {
        display: flex;
        flex-direction: column;

        &.user-message {
          align-items: flex-end;
          .message-bubble {
            background: $gaia-green;
            color: white;
            border-radius: 18px 18px 2px 18px;
          }
        }

        &.bot-message {
          align-items: flex-start;
          .message-bubble {
            background: white;
            color: $gaia-text;
            border-radius: 18px 18px 18px 2px;
            border: 1px solid rgba($gaia-green, 0.18);

            // Markdown output styles
            ::v-deep p { margin: 0 0 6px; &:last-child { margin-bottom: 0; } }
            ::v-deep ul { margin: 4px 0 4px 16px; padding: 0; }
            ::v-deep li { margin-bottom: 2px; }
            ::v-deep strong { font-weight: 600; }
            ::v-deep code {
              background: rgba($gaia-green, 0.1);
              border-radius: 3px;
              padding: 1px 4px;
              font-size: 12px;
              font-family: monospace;
            }
          }
        }

        &.error-message .message-bubble {
          background: #fff3f3;
          border-color: rgba(#dc3545, 0.3);
          color: #842029;
        }

        .message-bubble {
          max-width: 82%;
          padding: 9px 13px;
          font-size: 14px;
          line-height: 1.5;
          word-break: break-word;
          box-shadow: 0 1px 4px rgba(0,0,0,0.07);
        }

        .retry-btn {
          margin-top: 4px;
          align-self: flex-start;
          background: none;
          border: 1px solid rgba(#dc3545, 0.4);
          border-radius: 12px;
          color: #dc3545;
          font-size: 12px;
          padding: 3px 10px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 3px;
          transition: background 0.15s;

          &:hover { background: rgba(#dc3545, 0.08); }
          .material-icons { font-size: 14px; }
        }
      }

      // ── Typing indicator ───────────────────────────────────────
      .typing-indicator {
        display: flex;
        align-items: center;
        gap: 5px;
        padding: 12px 14px;

        span {
          width: 7px;
          height: 7px;
          background: rgba($gaia-green, 0.55);
          border-radius: 50%;
          animation: typing-bounce 1.2s infinite ease-in-out;

          &:nth-child(2) { animation-delay: 0.2s; }
          &:nth-child(3) { animation-delay: 0.4s; }
        }
      }
    }

    // ── Quick-action chips ───────────────────────────────────────
    .chatbot-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      padding: 8px 12px;
      border-top: 1px solid $gaia-earth-dark;
      background: $gaia-earth;
      flex-shrink: 0;

      .chip-btn {
        background: white;
        border: 1.5px solid rgba($gaia-green, 0.4);
        border-radius: 14px;
        color: $gaia-text;
        font-size: 12px;
        padding: 5px 10px;
        cursor: pointer;
        transition: background 0.15s, border-color 0.15s;
        white-space: nowrap;

        &:hover:not(:disabled) {
          background: rgba($gaia-green, 0.08);
          border-color: $gaia-green;
        }

        &:disabled { opacity: 0.5; cursor: not-allowed; }
      }
    }

    // ── Input bar ────────────────────────────────────────────────
    .chatbot-input {
      padding: 10px;
      border-top: 1px solid $gaia-earth-dark;
      background: white;
      display: flex;
      gap: 8px;
      align-items: center;
      flex-shrink: 0;

      input {
        flex: 1;
        border: 1.5px solid #c8dfc9;
        border-radius: 20px;
        padding: 8px 14px;
        font-size: 14px;
        background: $gaia-earth;
        color: $gaia-text;
        outline: none;
        transition: border-color 0.2s;

        &::placeholder { color: #8fa98f; font-style: italic; }
        &:focus         { border-color: $gaia-green; }
        &:disabled      { background: $gaia-earth-dark; opacity: 0.6; }
      }

      .send-btn {
        border-radius: 50%;
        width: 38px;
        height: 38px;
        min-width: 38px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: $gaia-green;
        border: none;
        color: white;
        cursor: pointer;
        transition: background 0.2s, transform 0.15s;
        box-shadow: 0 2px 6px rgba($gaia-green, 0.4);

        &:hover:not(:disabled)  { background: $gaia-green-dark; transform: scale(1.06); }
        &:disabled              { background: #aaa; box-shadow: none; cursor: not-allowed; }

        .material-icons { font-size: 18px; }
      }
    }
  }
}
</style>
